import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import MapaDaEntrega from "@/ao-vivo/MapaDaEntrega";
import { API_URL } from "@/lib/api";

// betracking.com.br/mapa/<chave do acompanhamento>: só o mapa da entrega ao
// vivo, pra outros sistemas embutirem num quadro (o card do pedido e os
// Envios individuais do dashboard). O mapa existe só aqui no betracking
// (Murilo, 20/10/2026: um lugar só, quem precisa embute).
//
// Parâmetros: ?altura=<px> (altura do mapa), ?cartao=1 (reserva espaço
// embaixo pro cartão que quem embute põe por cima) e ?som=0 (sem o som da
// chegada, quando quem embute já toca o dele) e ?telacheia=0 (sem o botão
// de tela cheia: quem embute amplia o quadro inteiro, com o cartão dele).
// Em quadro, o mapa ocupa a altura do quadro (100vh dentro dele): quem embute
// muda a altura do quadro (na tela cheia, por exemplo) e o mapa acompanha.
//
// Avisos pra quem embute (postMessage, só números e o status):
//  - { tipo: "betracking:mapa:altura", altura } — o tamanho do conteúdo
//    (0 quando não há mapa pra mostrar);
//  - { tipo: "betracking:mapa:rota", rota: { metros, segundos } | null };
//  - { tipo: "betracking:mapa:entrega", status };
//  - { tipo: "betracking:mapa:pronto" } — pode mandar os logos.
// E recebe { tipo: "betracking:mapa:logos", logos: [url, …] }: os logos da
// empresa pro pino do destino (até 5, só https); e
// { tipo: "betracking:mapa:chave", token }: a chave por mensagem, pra quem
// embute abrir só /mapa, sem a chave no endereço do quadro (as gravações de
// sessão do dashboard leem o endereço; a mensagem não fica na página).
// Aberto direto, fora de um quadro, vai pra página de rastreio da entrega.
const avisar = (dados: Record<string, unknown>) => {
  if (window.parent !== window) window.parent.postMessage(dados, "*");
};

export default function MapaEmbutido() {
  const { token: tokenDaRota = "" } = useParams<{ token: string }>();
  const [tokenRecebido, setTokenRecebido] = useState("");
  const token = tokenDaRota || tokenRecebido;
  const busca = new URLSearchParams(window.location.search);
  const altura = Math.min(900, Math.max(200, Number(busca.get("altura")) || 420));
  const caixa = useRef<HTMLDivElement>(null);
  const emQuadro = window.parent !== window;
  const [logos, setLogos] = useState<string[]>([]);

  useEffect(() => {
    const ouvir = (e: MessageEvent) => {
      if (e.source !== window.parent) return;
      if (e.data?.tipo === "betracking:mapa:chave" && typeof e.data.token === "string" && /^[A-Za-z0-9_-]{8,64}$/.test(e.data.token)) {
        setTokenRecebido(e.data.token);
      }
      if (e.data?.tipo === "betracking:mapa:logos" && Array.isArray(e.data.logos)) {
        setLogos(e.data.logos.filter((u: unknown) => typeof u === "string" && /^https:\/\//.test(u)).slice(0, 5));
      }
    };
    window.addEventListener("message", ouvir);
    avisar({ tipo: "betracking:mapa:pronto" });
    return () => window.removeEventListener("message", ouvir);
  }, []);

  useEffect(() => {
    document.title = "Mapa da entrega · betracking";
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    if (!caixa.current) return;
    const medir = () => avisar({ tipo: "betracking:mapa:altura", altura: Math.ceil(caixa.current?.getBoundingClientRect().height ?? 0) });
    const obs = new ResizeObserver(medir);
    obs.observe(caixa.current);
    medir();
    return () => obs.disconnect();
  }, []);

  const aoMudarRota = useCallback((rota: { metros: number; segundos: number } | null) => {
    avisar({ tipo: "betracking:mapa:rota", rota: rota ? { metros: rota.metros, segundos: rota.segundos } : null });
  }, []);
  const aoMudarEntrega = useCallback(
    (view: { status?: string; trackingUrl?: string }) => {
      avisar({ tipo: "betracking:mapa:entrega", status: view.status ?? null });
      if (!emQuadro && view.trackingUrl) window.location.replace(view.trackingUrl);
    },
    [emQuadro]
  );

  return (
    <div ref={caixa}>
      {/* Esperando a chave: guarda o lugar do mapa. */}
      {!token && <div style={{ height: emQuadro ? "100vh" : altura }} className="animate-pulse bg-[#F3EEE9]" />}
      {/* Sem o botão de tela cheia, os controles do mapa descem pro lugar do
          botão de ampliar de quem embute (no canto de cima, à direita). */}
      {busca.get("telacheia") === "0" && <style>{".maplibregl-ctrl-top-right{top:44px}"}</style>}
      {token && <MapaDaEntrega
        token={token}
        apiUrl={API_URL}
        altura={emQuadro ? "100vh" : `${altura}px`}
        comLinha={false}
        folgaParaCartao={busca.get("cartao") === "1"}
        onRota={aoMudarRota}
        onView={aoMudarEntrega}
        logos={logos}
        comSom={busca.get("som") !== "0"}
        semTelaCheia={busca.get("telacheia") === "0"}
      />}
    </div>
  );
}
